/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';
import ReactNotification from 'react-notifications-component';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import 'react-notifications-component/dist/theme.css';

import HomePage from 'pages/Home';
import BookDemo from 'pages/BookDemo';
import AccountSettings from 'pages/Settings';
import Favorites from 'pages/Favorites';
import Orders from 'pages/Orders';
import MadeForYou from 'pages/MadeForYou';
import BrowseRestaurants from 'pages/BrowseRestaurants';
import PresetDetail from 'pages/PresetDetail';
import RestaurantDetails from 'pages/RestaurantDetails';
import Checkout from 'pages/Checkout';
import CheckoutCompleted from 'pages/Checkout/CheckoutCompleted';
import Header from 'components/common/Header';
import Footer from 'components/common/Footer';
import Sidebar from 'components/common/Sidebar';
import PageNotFound from 'components/PageNotFound';
import RouteLoader from 'components/common/RouteLoader';
import SignUpModal from 'components/common/SignUpModal';
import SignInModal from 'components/common/SignInModal';
import ForgotPasswordModal from 'components/common/ForgotPasswordModal';
import SignUpConfirmModal from 'components/common/SignUpConfirmModal';
import { showSignUpModal } from 'redux/actions/modalActions';

// import 'bootstrap/dist/css/bootstrap.min.css';
// don't need bootstrap css as our design is based on custom libs.css
// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';
// don't need this input here. Instead we will use downloaded CDN inside assets
import './App.css';
import ScrollToTop from 'components/common/ScrollToTop';
import AboutUS from 'pages/AboutUs';
import OrderEdit from 'pages/OrderEdit';
import Faq from 'pages/Faq';
import Contact from 'pages/Contact';
import Pricing from 'pages/Pricing';
import Terms from 'pages/Terms';
import DeliveryAreas from 'pages/DeliveryAreas';
import ResetPassword from 'pages/ReserPassword';
import Payments from 'pages/Payments';
import AddLastMinuteItem from 'pages/AddLastMinuteItem';
import AddonCompleted from 'pages/Checkout/AddonCompleted';
import Report from 'pages/Report';
import SignUp from 'pages/SignUp';
import OrderDeliveryConfirmation from 'pages/OrderDeliveryConfirmation';
import OrderFeedbackPage from 'pages/OrderFeedback';

Sentry.init({
  dsn: process.env.REACT_SENTRY_DSN,
  integrations: [new BrowserTracing()],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.1,
});

function App() {
  const user = useSelector(state => get(state, 'user.user'));
  const userSignedIn = user && user.confirmed;
  const { search, pathname } = useLocation();
  const dispatch = useDispatch();
  const location = useLocation();

  const sidebarPaths = [
    '/settings',
    '/payments',
    '/favorites',
    '/reports',
    '/my-orders',
  ];

  const showSidebar = userSignedIn && sidebarPaths.includes(pathname);
  const showFooter = !sidebarPaths.includes(pathname);

  return (
    <>
      <Helmet>
        <title>
          PandaOnWheels | Great food, on its way. Fast, delicious meals
          delivered to your office because happy teams do their best.
        </title>
        <meta
          name="description"
          content="PandaOnWheels delivers delicious meals fast to your office. Custom menus, live tracking, and on-time delivery that keeps your whole team smiling."
        />
      </Helmet>

      <div className="app-layout">
        <ReactNotification />
        <Header />
        {showSidebar && <Sidebar />}
        <div className={`app-content${showSidebar ? ' content-with-sidebar' : ''}`}>
          <RouteLoader>
          <Switch>
            <Redirect from="/confirmation" to={`/${search}`} />
            <Route
              exact
              path="/"
              component={userSignedIn ? BrowseRestaurants : HomePage}
            />
            <Route exact path="/book-demo" component={BookDemo} />
            <Route exact path="/faq" component={Faq} />
            <Route exact path="/contact" component={Contact} />
            <Route exact path="/pricing" component={Pricing} />
            <Route exact path="/terms" component={Terms} />
            <Route exact path="/delivery-areas" component={DeliveryAreas} />
            <Route
              exact
              path="/accounts/reset-password"
              component={ResetPassword}
            />
            <Route exact path="/settings" component={AccountSettings} />
            <Route
              exact
              path="/payments"
              render={() => (userSignedIn ? <Payments /> : <Redirect to="/" />)}
              // component={Payments}
            />
            <Route exact path="/my-orders" component={Orders} />
            <Route exact path="/favorites" component={Favorites} />
            <Route exact path="/reports" component={Report} />
            <Route exact path="/browse-presets" component={MadeForYou} />
            <Route
              exact
              path="/browse-restaurants"
              component={BrowseRestaurants}
            />
            <Route exact path="/preset-detail/:id" component={PresetDetail} />
            <Route exact path="/chefs/:slug" component={RestaurantDetails} />
            <Route exact path="/chefs/:slug/:order_id" component={OrderEdit} />
            <Route exact path="/checkout" component={Checkout} />
            <Route
              exact
              path="/checkout-completed"
              component={CheckoutCompleted}
            />
            <Route exact path="/about-us" component={AboutUS} />
            <Route exact path="/signup" component={SignUp} />
            <Route
              exact
              path="/complete-addon-order"
              component={AddonCompleted}
            />
            <Route
              exact
              path="/add_on_order/chefs"
              component={AddLastMinuteItem}
            />
            <Route exact path="/add_on_checkout" component={Checkout} />
            <Route
              exact
              path="/order/:orderId/order-delivery-confirmation"
              component={OrderDeliveryConfirmation}
            />
            <Route
              path="/orders/:orderId/order-feedback"
              component={OrderFeedbackPage}
            />
            <Route component={PageNotFound} />
          </Switch>
          </RouteLoader>
          <ScrollToTop />
          {/* Because when path is changed, we should go to top of the page */}
          <SignInModal />
          <SignUpModal />
          <SignUpConfirmModal />
          <ForgotPasswordModal />
        </div>
        {showFooter && <Footer />}
      </div>
    </>
  );
}

export default App;
